// Candidate page (plan §8; Milestone 2 writes; Milestone 2.5 tracks): header +
// change status, visibility for tonight + the next `lookahead_nights` nights,
// image timeline, follow-up tracks (plan §6.2) with their tasks, add-task /
// trigger form, observation log, suggested next steps, threaded comments.
//
// Milestone 1 has no charting dependency, so "visibility panel" renders as
// a per-night summary card (window, best altitude/airmass, min Moon
// separation) rather than a plotted altitude curve — the same numbers a
// curve would be read off of.

import { loadCandidateDetail, refreshCandidate, facilityById, buildTaskFromIssue, mergeTasks } from '../lib/data.js';
import {
  getStatus,
  findPerson,
  nextStepsFor,
  transitionsFor,
  isBackwardTransition,
  requiredFieldsFor,
  vocabulary,
  resolveRole,
  tracksForStatus,
  tasksForTrack,
  trackState,
  eligibleImages,
  trackInstruments,
  predictedArrivals,
  referenceImage,
  rolesForStatus,
  newlyRelevantRoles,
  assignablePeople,
  taskDueDate,
  isTerminal,
  arrivalAnchor,
  dateFromDelay,
} from '../lib/rules.js';
import { nightlyVisibility, upcomingVisibility } from '../lib/visibility.js';
import { createIssue, setIssueAssignees } from '../lib/github.js';
import {
  buildTaskIssue,
  buildTriggerMailto,
  changeCandidateStatus,
  issueStateFor,
  updateCandidateFields,
  setTaskTrack,
  TASK_TYPES,
  TASK_TYPE_LABELS,
  REDUCTION_STATUSES,
  appendTriggerObservation,
  ANALYSIS_PRODUCTS,
  INSTRUMENT_MODES,
} from '../lib/write.js';
import { escapeHtml, statusPillHtml, formatUtc, formatMinutes } from '../lib/format.js';
import { dateInputHtml, utcDateTimeInputHtml, utcDateTimeValue } from '../lib/dateinput.js';

function findCandidate(candidates, routeId) {
  if (routeId.startsWith('issue-')) {
    const number = Number(routeId.slice('issue-'.length));
    return candidates.find((c) => c.issue.number === number);
  }
  return candidates.find((c) => c.data?.id === routeId);
}

function renderNightCard(night) {
  if (!night.visible) {
    return `<div class="night-card"><h3>${night.night}</h3><div class="no-window">not visible</div></div>`;
  }
  return `
    <div class="night-card">
      <h3>${night.night}</h3>
      <div>${formatUtc(night.windowStart)} – ${formatUtc(night.windowEnd)}</div>
      <div class="muted">
        ${formatMinutes(night.durationMinutes)} ·
        alt ${night.bestAltitudeDeg.toFixed(0)}° ·
        airmass ${night.bestAirmass.toFixed(2)} ·
        moon ${night.minMoonSeparationDeg.toFixed(0)}°
      </div>
    </div>`;
}

function renderVisibility(candidateData, facilities, rules) {
  if (!candidateData?.ra_deg && candidateData?.ra_deg !== 0) {
    return '<p class="muted">No coordinates parsed for this candidate.</p>';
  }
  return facilities
    .map((facility) => {
      if (facility.space_based) {
        return `
          <h3>${escapeHtml(facility.name)}</h3>
          <p class="muted">Space-based facility — no ground site, so nightly altitude/airmass visibility doesn't apply. Scheduling follows its own sun-avoidance/roll constraints instead.</p>`;
      }
      const nights = upcomingVisibility({
        raDeg: candidateData.ra_deg,
        decDeg: candidateData.dec_deg,
        site: facility.site,
        minAltitudeDeg: facility.min_altitude_deg,
        maxAirmass: facility.max_airmass,
        rules,
      });
      const cards = nights.map(renderNightCard).join('');
      return `<h3>${escapeHtml(facility.name)}</h3><div class="visibility-grid">${cards}</div>`;
    })
    .join('');
}

function roleLine(data, people, roleId) {
  const { personId, source } = resolveRole(data, roleId, people);
  const name = personId ? findPerson(people, personId)?.name ?? personId : 'unassigned';
  const tag = source === 'default' ? ' <span class="inherited">(group default)</span>' : '';
  const cls = source === 'unassigned' ? 'unassigned' : '';
  return `<li><strong>${escapeHtml(roleId)}:</strong> <span class="${cls}">${escapeHtml(name)}</span>${tag}</li>`;
}

/** Dropdown for one role: "" = group default (writes nothing), else a person id. */
function roleSelect(name, roleId, people, currentOverride) {
  const holderId = people.roles?.[roleId]?.holder;
  const holder = holderId ? findPerson(people, holderId)?.name ?? holderId : 'unassigned';
  const options = assignablePeople(people, currentOverride)
    .map((p) => `<option value="${escapeHtml(p.id)}" ${p.id === currentOverride ? 'selected' : ''}>${escapeHtml(p.name)} (${escapeHtml(p.id)})</option>`)
    .join('');
  return `<select name="${escapeHtml(name)}"><option value="" ${currentOverride ? '' : 'selected'}>Group default (${escapeHtml(holder)})</option>${options}</select>`;
}

/**
 * Roles card: the phase's relevant roles (rules.yaml `roles:`) up front, the
 * rest folded away; "Edit roles" swaps in a form of dropdowns that writes
 * `roles_override` (plan §4.1 level 1). "Group default" removes the pin.
 */
function renderRolesCard(data, rules, people) {
  const all = Object.keys(people.roles ?? {});
  const relevant = rolesForStatus(rules, data.status, people).filter((r) => all.includes(r));
  const others = all.filter((r) => !relevant.includes(r));
  const phase = getStatus(rules, data.status)?.label ?? data.status;
  const overrides = data.roles_override ?? {};
  const editRow = (roleId) => `<label>${escapeHtml(roleId)} ${roleSelect(`roleov:${roleId}`, roleId, people, overrides[roleId])}</label>`;

  return `
    <div class="card" id="roles-card">
      <div class="track-head">
        <h2>Roles <span class="muted">(${escapeHtml(phase)})</span></h2>
        <button type="button" class="linklike" id="edit-roles-btn">Edit roles</button>
      </div>
      <div id="roles-view">
        <ul class="next-steps">${relevant.map((r) => roleLine(data, people, r)).join('')}</ul>
        ${
          others.length
            ? `<details><summary class="muted">Not active in this phase (${others.length})</summary><ul class="next-steps">${others
                .map((r) => roleLine(data, people, r))
                .join('')}</ul></details>`
            : ''
        }
      </div>
      <form id="roles-form" hidden>
        <p class="muted">"Group default" follows people.yaml; choosing a person pins that role for this candidate only.</p>
        ${relevant.map(editRow).join('')}
        ${others.length ? `<details><summary class="muted">Roles not active in this phase</summary>${others.map(editRow).join('')}</details>` : ''}
        <div class="form-actions">
          <button type="submit">Save roles</button>
          <button type="button" class="linklike" id="cancel-roles-btn">Cancel</button>
          <span id="roles-error" class="error"></span>
        </div>
      </form>
    </div>`;
}

/** Read `roleov:<role>` selects from a form into a full roles_override object (base kept for roles not in the form). */
function collectRoleOverrides(form, base = {}) {
  const next = { ...base };
  form.querySelectorAll('select[name^="roleov:"]').forEach((sel) => {
    const roleId = sel.name.slice('roleov:'.length);
    if (sel.value) next[roleId] = sel.value;
    else delete next[roleId];
  });
  return next;
}

// ---------- compact task lines (track cards and every task list) ----------

const todayUtc = () => new Date().toISOString().slice(0, 10);

/** "SS" for "Sherry Suyu" — first letters of the first and last word of the name. */
function initials(name) {
  const words = String(name).replace(/\s+—.*$/, '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** What the task *is*, without the "[cand-id] type:" / "(image X)" boilerplate the title carries. */
function taskWhat(t, facilities) {
  const d = t.data ?? {};
  if (t.type === 'archival') return `${d.source ?? '?'}${d.instrument ? ` · ${d.instrument}` : ''}`;
  if (d.facility || d.instrument) {
    const facility = facilityById(facilities, d.facility);
    const instrument = facility?.instruments?.find((i) => i.id === d.instrument);
    const band = d.filter ? ` (${d.filter})` : '';
    return `${instrument?.name ?? d.instrument ?? '?'}${band} · ${facility?.short ?? (d.facility ?? '?').toUpperCase()}`;
  }
  return t.issue.title.replace(/^\[[^\]]*\]\s*/, '').replace(/^(trigger|archival|analysis|decision):\s*/, '').replace(/\s*\(image [^)]*\)$/, '');
}

/** The one date that matters for this task, plus a recurring campaign's cadence. */
function taskWhen(t) {
  const d = t.data ?? {};
  const parts = [];
  if (d.cadence_days) parts.push(`every ${d.cadence_days} d${d.until ? ` until ${d.until}` : ''}`);
  if (d.requested_date) parts.push(`req. ${d.requested_date}`);
  else if (d.deadline) parts.push(`due ${d.deadline}`);
  if (t.type === 'archival' && (d.date_start || d.date_end)) parts.push(`data ${d.date_start ?? '…'} – ${d.date_end ?? '…'}`);
  const nights = triggerObservations(t);
  if (nights.length) {
    const last = nights.map((o) => String(o.obs_utc ?? '').slice(0, 10)).filter(Boolean).sort().pop();
    parts.push(`${nights.length} night${nights.length === 1 ? '' : 's'} observed${last ? ` · last ${last}` : ''}`);
  }
  return parts.join(' · ');
}

/** A trigger's logged nights of data (plan §8 Milestone 2.6); [] for anything else. */
function triggerObservations(t) {
  return t.type === 'trigger' && Array.isArray(t.data?.observations) ? t.data.observations : [];
}

/** open / closed / overdue (open, its requested date or deadline has passed, and — for a trigger — no data logged yet). */
function taskLineState(t) {
  if (t.issue.state !== 'open') return 'closed';
  if (triggerObservations(t).length) return 'open'; // data is arriving; a campaign stays open by design
  const due = taskDueDate(t);
  return due && String(due).slice(0, 10) < todayUtc() ? 'overdue' : 'open';
}
const TASK_STATE_TITLE = { open: 'open', closed: 'closed', overdue: 'open — its requested date / deadline has passed with no data logged' };

/**
 * One task as a single compact line: state dot, what (instrument · facility),
 * when, assignee initials, #number linking to GitHub. `opts.track` names the
 * task's track (lists that mix tracks); the task type is shown only when it
 * differs from its track's usual `task_type` (`opts.typeUnless` overrides that); `opts.moveTo` adds the
 * "move to…" control (untracked tasks).
 */
function renderTaskLine(t, ctx, opts = {}) {
  const state = taskLineState(t);
  const when = taskWhen(t);
  const trackLabel = opts.track && t.data?.track ? ctx.rules.tracks?.find((tr) => tr.id === t.data.track)?.label ?? t.data.track : '';
  // Show the type only when it's not what the task's track normally holds (e.g. archival data in a trigger track).
  const usualType = opts.typeUnless ?? ctx.rules.tracks?.find((tr) => tr.id === t.data?.track)?.task_type;
  const typeTag = t.type !== usualType ? `<span class="task-type">${escapeHtml(t.type)}</span>` : '';
  const people = (t.issue.assignees ?? [])
    .map((a) => {
      const name = findPerson(ctx.people, a.login)?.name ?? a.login;
      return `<span class="avatar" title="${escapeHtml(`${name} (${a.login})`)}">${escapeHtml(initials(name))}</span>`;
    })
    .join('');

  let move = '';
  if (opts.moveTo) {
    const choices = opts.moveTo.filter((tr) => tr.id !== t.data?.track && (!tr.per_image || t.data?.image));
    if (t.data && choices.length) {
      move = `<span class="task-move"><select data-move-select="${t.issue.number}" aria-label="Move #${t.issue.number} to track"><option value="">move to…</option>${optionEls(
        choices.map((tr) => tr.id),
        choices.map((tr) => tr.label ?? tr.id),
      )}</select> <button type="button" class="small-btn" data-move-task="${t.issue.number}">Move</button></span>`;
    }
  }

  return `
    <li class="task-line state-${state}">
      <span class="task-dot" title="${TASK_STATE_TITLE[state]}"></span>
      <span class="task-main">
        <span class="task-what">${typeTag}${escapeHtml(taskWhat(t, ctx.facilities))}${t.data?.image && opts.image ? ` <span class="muted">· image ${escapeHtml(t.data.image)}</span>` : ''}</span>
        ${when || trackLabel ? `<span class="task-when">${escapeHtml([trackLabel, when].filter(Boolean).join(' · '))}</span>` : ''}
        ${
          t.type === 'trigger' && t.data
            ? `<span class="task-actions"><button type="button" class="task-action-btn" data-log-obs="${t.issue.number}" title="Record a night of data taken for this trigger">✎ Log observation</button></span>`
            : ''
        }
      </span>
      <span class="task-people">${people || '<span class="avatar empty" title="No assignee — assign someone on GitHub">?</span>'}</span>
      <a class="task-num" href="${t.issue.html_url}" target="_blank" rel="noreferrer" title="${escapeHtml(t.issue.title)}">#${t.issue.number}↗</a>
      ${move}
    </li>`;
}

/** A list of task lines: open first, closed dimmed after them, and folded away once there are 3+ closed. */
function renderTaskLines(tasks, ctx, opts = {}) {
  if (!tasks.length) return opts.empty === '' ? '' : `<p class="muted">${opts.empty ?? 'No tasks yet.'}</p>`;
  const byNewest = (a, b) => b.issue.number - a.issue.number;
  const open = tasks.filter((t) => t.issue.state === 'open').sort(byNewest);
  const closed = tasks.filter((t) => t.issue.state !== 'open').sort(byNewest);
  const lines = (list) => list.map((t) => renderTaskLine(t, ctx, opts)).join('');
  const closedHtml =
    closed.length >= 3
      ? `<li class="task-folded"><details><summary>${closed.length} closed</summary><ul class="task-lines">${lines(closed)}</ul></details></li>`
      : lines(closed);
  return `<ul class="task-lines">${lines(open)}${closedHtml}</ul>`;
}

// ---------- image timeline (plan §5.1 image_dates) ----------

const IMAGE_FIELDS = ['detected', 'peak', 'faded'];

/** Every image label we know of for this candidate, sorted: reference, image_dates, predicted delays, image_positions, n_images. */
function imageLabels(data) {
  const labels = new Set([referenceImage(data)]);
  for (const key of Object.keys(data?.image_dates ?? {})) labels.add(key);
  for (const key of Object.keys(data?.time_delays?.predicted ?? {})) labels.add(key);
  for (const pos of data?.lens?.image_positions ?? []) if (pos?.label) labels.add(String(pos.label));
  const n = Number(data?.lens?.n_images);
  if (Number.isInteger(n) && n > 0 && n <= 26) for (let i = 0; i < n; i++) labels.add(String.fromCharCode(65 + i));
  return [...labels].sort();
}

/** True when there is at least one known image and every known image has a `faded` date. */
function allImagesFaded(data) {
  const images = imageLabels(data);
  return images.length > 0 && images.every((image) => data?.image_dates?.[image]?.faded);
}

const DECIMAL_PATTERN = '[0-9]+\\.?[0-9]*|\\.[0-9]+';
const SIGNED_DECIMAL_PATTERN = '[-+]?([0-9]+\\.?[0-9]*|\\.[0-9]+)';

function renderImageTimeline(data) {
  const ref = referenceImage(data);
  const anchor = arrivalAnchor(data);
  const td = data?.time_delays ?? {};
  const arrivals = predictedArrivals(data);
  const dateInput = (image, field) =>
    dateInputHtml(`${field}:${image}`, data?.image_dates?.[image]?.[field] ?? '', { ariaLabel: `Image ${image} ${field}` });
  // The time delay (time_delays.predicted, days relative to the reference
  // image) is what's entered and stored; the arrival date is derived from it.
  const delayInputs = (image, delay, err) => `
    <span class="delay-cell">
      <input class="delay-input" type="text" inputmode="decimal" pattern="${SIGNED_DECIMAL_PATTERN}" title="Days relative to image ${escapeHtml(ref)}, e.g. 9.5 (may be negative)" name="delay:${escapeHtml(image)}" value="${escapeHtml(delay ?? '')}" aria-label="Estimated time delay of image ${escapeHtml(image)} in days" />
      ± <input class="err-input" type="text" inputmode="decimal" pattern="${DECIMAL_PATTERN}" title="1σ uncertainty in days, e.g. 2.5" name="delay_err:${escapeHtml(image)}" value="${escapeHtml(err ?? '')}" aria-label="1σ uncertainty of image ${escapeHtml(image)}'s time delay in days" /> d
    </span>`;
  const arrivalCell = (image, delay) => `<span class="muted" data-arrival="${escapeHtml(image)}">${escapeHtml(dateFromDelay(anchor, delay) ?? '—')}</span>`;

  const rows = imageLabels(data)
    .map((image) => {
      const delayCell = image === ref ? '<span class="muted">reference image</span>' : delayInputs(image, td.predicted?.[image], td.predicted_err?.[image]);
      const arrival = image === ref ? '' : arrivalCell(image, td.predicted?.[image]);
      return `<tr><td><strong>${escapeHtml(image)}</strong></td>${IMAGE_FIELDS.map((f) => `<td>${dateInput(image, f)}</td>`).join('')}<td>${delayCell}</td><td>${arrival}</td></tr>`;
    })
    .join('');

  const next = arrivals[0];
  const summary = next
    ? `Next image expected: <strong>${escapeHtml(next.image)}</strong> ~${escapeHtml(next.date)}`
    : 'No predicted arrivals yet — enter estimated time delays below.';
  const anchorNote = anchor
    ? `Time delays are relative to ${data?.image_dates?.[ref]?.detected ? `image ${escapeHtml(ref)}'s detected date` : `the discovery date (until image ${escapeHtml(ref)} is detected)`} (${escapeHtml(anchor)}); predicted arrival = that date + delay.`
    : `Predicted arrivals appear once image ${escapeHtml(ref)}'s detected date (or the candidate's discovery date) is set.`;

  return `
    <div class="card" id="image-timeline-card">
      <h2>Image timeline</h2>
      <p class="muted">${summary}</p>
      <form id="image-timeline-form">
        <div class="table-scroll">
          <table class="compact">
            <thead><tr><th>Image</th><th>Detected</th><th>Peak</th><th>Faded</th><th>Estimated time delay ± 1<span class="nocase">σ</span></th><th>Predicted arrival</th></tr></thead>
            <tbody>
              ${rows}
              <tr>
                <td><input name="newImage" placeholder="add…" maxlength="3" /></td>
                ${IMAGE_FIELDS.map((f) => `<td>${dateInputHtml(`${f}:__new`, '', { ariaLabel: `New image ${f}` })}</td>`).join('')}
                <td>${delayInputs('__new', null, null)}</td>
                <td>${arrivalCell('__new', null)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="muted small">${anchorNote}</p>
        <div class="form-actions">
          <button type="submit">Save image timeline</button>
          <span id="image-timeline-error" class="error"></span>
        </div>
      </form>
    </div>`;
}

// ---------- follow-up tracks (plan §6.2) ----------

// Explicit "no track" choice in the Add-task Track select (vs. not chosen yet).
const UNTRACKED = '__untracked';

const TRACK_STATE_LABEL = { waiting: 'waiting for event', not_started: 'not started', active: 'active', done: 'done' };

function renderTrackCard(ctx, track, data, tasks) {
  const { people } = ctx;
  const state = trackState(track, tasks, data);
  const trackTasks = tasksForTrack(track, tasks);
  const owner = track.role ? resolveRole(data, track.role, people) : null;
  const ownerName = owner?.personId ? findPerson(people, owner.personId)?.name ?? owner.personId : 'unassigned';
  const lineOpts = { typeUnless: track.task_type };
  const addBtn = (label, image) =>
    `<button type="button" class="small-btn" data-add-task data-track="${escapeHtml(track.id)}"${image ? ` data-image="${escapeHtml(image)}"` : ''}>${escapeHtml(label)}</button>`;

  const meta = [
    `<span title="${escapeHtml(track.role ?? '')}">Owner: ${escapeHtml(ownerName)}</span>`,
    track.default_cadence_days ? `default every ${escapeHtml(String(track.default_cadence_days))} d` : '',
    track.optional ? '<span title="Not every candidate needs this track">optional</span>' : '',
  ].filter(Boolean);
  const purposes = (track.purposes ?? []).map((p) => `<li>${escapeHtml(p)}</li>`).join('');

  let body;
  if (track.per_image) {
    // One group per detected trailing image, each with its own tasks and "+ Add";
    // a task naming an image that isn't (or is no longer) eligible still shows, under its image.
    const eligible = eligibleImages(track, data, tasks);
    const groups = new Map(eligible.map((e) => [e.image, { detected: e.detected, tasks: [] }]));
    for (const t of trackTasks) {
      const key = t.data?.image ?? '';
      if (!groups.has(key)) groups.set(key, { detected: null, tasks: [] });
      groups.get(key).tasks.push(t);
    }
    body = groups.size
      ? [...groups.entries()]
          .map(
            ([image, g]) => `
        <div class="image-group">
          <div class="image-group-head">
            <strong>${image ? `Image ${escapeHtml(image)}` : 'No image set'}</strong>
            <span class="muted">${g.detected ? `detected ${escapeHtml(g.detected)}` : ''}${g.tasks.length ? '' : `${g.detected ? ' · ' : ''}not targeted`}</span>
          </div>
          ${renderTaskLines(g.tasks, ctx, { ...lineOpts, empty: '' })}
          ${image && g.detected ? addBtn(g.tasks.length ? '+ Add another task' : '+ Add task', image) : ''}
        </div>`,
          )
          .join('')
      : '<p class="muted">Waiting for a trailing image to be detected — record its date in the Image timeline above.</p>';
  } else {
    body = `${renderTaskLines(trackTasks, ctx, lineOpts)}<div>${addBtn('+ Add task')}</div>`;
  }

  return `
    <div class="card track-card">
      <div class="track-head">
        <h3>${escapeHtml(track.label ?? track.id)}</h3>
        <span class="track-state state-${state}">${TRACK_STATE_LABEL[state]}</span>
      </div>
      <div class="track-meta">${meta.join('<span class="sep">·</span>')}${
        purposes ? `<details class="purpose"><summary>ⓘ Purpose</summary><ul class="purposes">${purposes}</ul></details>` : ''
      }</div>
      ${body}
    </div>`;
}

/**
 * Tasks filed under tracks of other phases, grouped by track with that track's
 * derived state (done / active …). Starts expanded while any of them is still
 * open, since earlier-phase work is normally finished before moving on.
 */
function renderOtherPhasesCard(ctx, data, tasks, earlier, title = 'Tasks from other phases') {
  const openCount = earlier.filter((t) => t.issue.state === 'open').length;
  const trackIds = [...new Set(earlier.map((t) => t.data.track))];
  const groups = (ctx.rules.tracks ?? [])
    .filter((track) => trackIds.includes(track.id))
    .map((track) => {
      const state = trackState(track, tasks, data);
      return `
        <div class="image-group">
          <div class="image-group-head">
            <strong>${escapeHtml(track.label ?? track.id)}</strong>
            <span class="track-state state-${state}">${TRACK_STATE_LABEL[state]}</span>
          </div>
          ${renderTaskLines(tasksForTrack(track, earlier), ctx, { typeUnless: track.task_type })}
        </div>`;
    })
    .join('');
  const summary = `${escapeHtml(title)} (${earlier.length}${openCount ? ` · <span class="open-count">${openCount} still open</span>` : ' · all done'})`;
  return `<details class="card other-phases"${openCount ? ' open' : ''}><summary><h3>${summary}</h3></summary>${title === 'Tasks from other phases' ? '<p class="muted">Filed under tracks that belong to another phase (e.g. confirmation triggers).</p>' : ''}${groups}</details>`;
}

/**
 * Track cards for the current phase, then tasks from other phases' tracks
 * (e.g. confirmation triggers once live — filed correctly, just not current),
 * then "Other tasks" (untracked / unknown track, with a "move to…" control).
 * Phases without tracks keep the flat table.
 */
function renderTrackSection(ctx, data, tasks) {
  const tracks = tracksForStatus(ctx.rules, data.status);
  if (!tracks.length) {
    // Phases without tracks (data_complete, false_positive): every tracked task by its track and state, then the rest.
    const knownTrack = new Set((ctx.rules.tracks ?? []).map((t) => t.id));
    const tracked = tasks.filter((t) => knownTrack.has(t.data?.track));
    const rest = tasks.filter((t) => !tracked.includes(t));
    return `<h2>Tasks</h2>${tracked.length ? renderOtherPhasesCard(ctx, data, tasks, tracked, 'Tasks by track') : ''}${
      rest.length || !tracked.length ? `<div class="card">${tracked.length ? '<h3>Other tasks</h3>' : ''}${renderTaskLines(rest, ctx)}</div>` : ''
    }`;
  }
  const inPhase = new Set(tracks.map((t) => t.id));
  const knownTrack = new Set((ctx.rules.tracks ?? []).map((t) => t.id));
  const earlier = tasks.filter((t) => t.data?.track && !inPhase.has(t.data.track) && knownTrack.has(t.data.track));
  const other = tasks.filter((t) => !inPhase.has(t.data?.track) && !earlier.includes(t));
  return `
    <h2>Follow-up tracks</h2>
    <div class="track-grid">${tracks.map((track) => renderTrackCard(ctx, track, data, tasks)).join('')}</div>
    ${
      earlier.length
        ? renderOtherPhasesCard(ctx, data, tasks, earlier)
        : ''
    }
    ${
      other.length
        ? `<div class="card"><h3>Other tasks</h3><p class="muted">Untracked, or with an unknown track. Use "move to…" to file a task under one of this phase's tracks.</p>${renderTaskLines(other, ctx, { moveTo: tracks })}<span id="move-task-error" class="error"></span></div>`
        : ''
    }`;
}

/**
 * Every night logged on a trigger plus every archival data set, newest first
 * (plan §8 Milestone 2.6) — the candidate's data inventory in one table.
 */
function renderObservationLog(tasks, ctx) {
  const link = (t) => `<a href="${t.issue.html_url}" target="_blank" rel="noreferrer">#${t.issue.number}</a>`;
  const location = (loc) =>
    !loc ? '—' : /^https?:\/\//.test(loc) ? `<a href="${escapeHtml(loc)}" target="_blank" rel="noreferrer">${escapeHtml(loc)}</a>` : escapeHtml(loc);
  const rows = [];
  for (const t of tasks) {
    if (t.type === 'trigger') {
      const facility = facilityById(ctx.facilities, t.data?.facility);
      const instrument = facility?.instruments?.find((i) => i.id === t.data?.instrument);
      for (const o of triggerObservations(t)) {
        const when = String(o.obs_utc ?? '').replace('T', ' ').replace(/:00Z$|Z$/, '');
        rows.push({
          sort: String(o.obs_utc ?? ''),
          cells: [when || '—', escapeHtml(facility?.short ?? t.data?.facility ?? '—'), escapeHtml(instrument?.name ?? t.data?.instrument ?? '—'), escapeHtml(o.setup ?? '—'), escapeHtml(o.conditions ?? '—'), location(o.data_location), escapeHtml(o.reduction_status ?? '—'), link(t)],
        });
      }
    } else if (t.type === 'archival') {
      const d = t.data ?? {};
      rows.push({
        sort: String(d.date_end ?? d.date_start ?? ''),
        cells: [`${d.date_start ?? '…'} – ${d.date_end ?? '…'}`, `${escapeHtml(d.source ?? '—')} <span class="task-type">archival</span>`, escapeHtml(d.instrument ?? '—'), '—', '—', location(d.data_location), escapeHtml(d.reduction_status ?? '—'), link(t)],
      });
    }
  }
  if (!rows.length) return '<p class="muted">No observations logged yet — use "+ Log observation" on a trigger, or add Archival observations.</p>';
  rows.sort((a, b) => b.sort.localeCompare(a.sort));
  const body = rows.map((r) => `<tr>${r.cells.map((c, i) => `<td>${i === 0 ? escapeHtml(c) : c}</td>`).join('')}</tr>`).join('');
  return `<div class="table-scroll"><table class="obs-log"><thead><tr><th>Date (UTC)</th><th>Facility / source</th><th>Instrument</th><th>Setup</th><th>Conditions</th><th>Data location</th><th>Reduction</th><th>Task</th></tr></thead><tbody>${body}</tbody></table></div>`;
}

function renderComments(comments) {
  if (!comments.length) return '<p class="muted">No comments yet.</p>';
  return comments
    .map(
      (c) => `
      <div class="card">
        <div class="muted">${escapeHtml(c.user?.login)} · ${formatUtc(c.created_at)}</div>
        <div>${escapeHtml(c.body)}</div>
      </div>`,
    )
    .join('');
}

function optionEls(values, labels = values) {
  return values.map((v, i) => `<option value="${escapeHtml(v)}">${escapeHtml(labels[i])}</option>`).join('');
}

/** "N tasks are still open — …" hint (with links), or '' when none are. */
function openTasksWarning(tasks, lead) {
  const open = tasks.filter((t) => t.issue.state === 'open');
  if (!open.length) return '';
  const links = open.map((t) => `<a href="${t.issue.html_url}" target="_blank" rel="noreferrer">#${t.issue.number}</a>`).join(', ');
  return `<p class="hint">${escapeHtml(lead)} ${open.length === 1 ? '1 task is' : `${open.length} tasks are`} still open (${links}) — close ${open.length === 1 ? 'it' : 'them'} on GitHub if no longer needed.</p>`;
}

function renderChangeStatus(rules, currentStatusId, data, tasks = []) {
  const { declared, other } = transitionsFor(rules, currentStatusId);
  // Hint only — never automatic (plan §8 M2.5 Step 4).
  const fadedHint =
    declared.some((d) => d.id === 'post_fade') && allImagesFaded(data)
      ? `<p class="hint">All images have a faded date — consider moving to <strong>${escapeHtml(getStatus(rules, 'post_fade')?.label ?? 'post_fade')}</strong>.</p>`
      : '';
  // A move to an earlier phase is labelled "↩ Back to …" so it doesn't read
  // like a different status; the text after the dash is the *reason* for the
  // move (it depends on where you're coming from), not part of the name.
  const optionText = (id, reason) => {
    const label = getStatus(rules, id)?.label ?? id;
    const name = isBackwardTransition(rules, currentStatusId, id) ? `↩ Back to ${label}` : label;
    return reason ? `${name} — ${reason}` : name;
  };
  const declaredOptions = declared.map((d) => `<option value="${escapeHtml(d.id)}">${escapeHtml(optionText(d.id, d.description))}</option>`).join('');
  const otherOptions = other.map((id) => `<option value="${escapeHtml(id)}">${escapeHtml(optionText(id))}</option>`).join('');
  return `
    <div class="card" id="change-status-card">
      <h2>Change status</h2>
      ${fadedHint}
      ${isTerminal(rules, currentStatusId) ? openTasksWarning(tasks, 'This candidate is finished, but') : ''}
      <form id="status-form">
        <label>New status
          <select name="newStatus">
            <option value="">— choose —</option>
            ${declaredOptions ? `<optgroup label="Declared transitions">${declaredOptions}</optgroup>` : ''}
            <optgroup label="Other">${otherOptions}</optgroup>
          </select>
        </label>
        <div id="status-extra-fields"></div>
        <div class="form-actions">
          <button type="submit">Update status</button>
          <span id="status-error" class="error"></span>
        </div>
      </form>
    </div>`;
}

function renderAddTask(facilities, tracks, rules) {
  // Suggestions only — any source can be typed (rules.yaml vocabularies.archival_sources).
  const sources = (rules?.vocabularies?.archival_sources ?? []).map((v) => (typeof v === 'string' ? v : v.label ?? v.id));
  const facilityOptions = optionEls(
    facilities.map((f) => f.id),
    facilities.map((f) => f.name),
  );
  const trackSelect = tracks.length
    ? `<label>Track
          <select name="track">
            <option value="" selected>— choose a track —</option>
            ${optionEls(
              tracks.map((t) => t.id),
              tracks.map((t) => t.label ?? t.id),
            )}
            <option value="${UNTRACKED}">Other (untracked)</option>
          </select>
        </label>
        <label data-role="image-wrap" hidden>Target image <select name="image"></select></label>
        <p class="muted" data-role="track-hint"></p>`
    : '';
  return `
    <div class="card" id="add-task-card">
      <h2>Add task</h2>
      <form id="add-task-form">
        ${trackSelect}
        <label>Type
          <select name="type">${optionEls(TASK_TYPES, TASK_TYPES.map((t) => TASK_TYPE_LABELS[t] ?? t))}</select>
        </label>

        <fieldset data-type="trigger">
          <label>Facility <select name="facility" data-role="facility">${facilityOptions}</select></label>
          <label>Instrument <select name="instrument" data-role="instrument"></select></label>
          <label>Mode <select name="mode" data-role="mode"></select></label>
          <label data-role="filter-wrap" hidden>Wavelength band <select name="filterBand" data-role="filter"></select></label>
          <label>Requested date ${dateInputHtml('requestedDate')}</label>
          <label>Cadence (days; blank = one-off) <input name="cadenceDays" type="number" min="0" step="any" /></label>
          <label>Until (end of a recurring campaign) ${dateInputHtml('until')}</label>
          <label data-role="images-wrap">Images <input name="images" placeholder="A, B" /></label>
        </fieldset>

        <fieldset data-type="archival" hidden>
          <p class="muted">Data we didn't request — survey, archive or another group's data. No PI involved; assigned to the track's lead.</p>
          <label>Source * <input name="source" list="archival-sources" placeholder="e.g. LSST" autocomplete="off" /></label>
          <datalist id="archival-sources">${sources.map((src) => `<option value="${escapeHtml(src)}"></option>`).join('')}</datalist>
          <label>Instrument / bands <input name="archivalInstrument" placeholder="e.g. LSSTCam ugrizy" /></label>
          <label>Data from ${dateInputHtml('dateStart')}</label>
          <label>Data until ${dateInputHtml('dateEnd')}</label>
          <label>Data location <input name="archivalLocation" placeholder="URL, archive id or path" /></label>
          <label>Reduction status <select name="archivalReduction">${optionEls(REDUCTION_STATUSES)}</select></label>
        </fieldset>

        <fieldset data-type="analysis" hidden>
          <label>Product <select name="product">${optionEls(ANALYSIS_PRODUCTS)}</select></label>
          <label>Result <textarea name="result" rows="2"></textarea></label>
          <label>Files <input name="files" placeholder="comma-separated paths/links" /></label>
        </fieldset>

        <fieldset data-type="decision" hidden>
          <label>Summary (used in the title) <input name="summary" placeholder="which facility to trigger?" /></label>
          <label>Deadline ${dateInputHtml('deadline')}</label>
          <label>Options <input name="options" placeholder="comma-separated" /></label>
        </fieldset>

        <div class="form-actions">
          <button type="submit">Create task</button>
          <span id="add-task-error" class="error"></span>
        </div>
      </form>
      <div id="trigger-mailto"></div>
    </div>`;
}

/**
 * Wire up the facility -> instrument -> mode(+filter, +images) cascade for
 * one group of selects. Optional pieces:
 * - `filterSelect`/`filterWrap`: a "Wavelength band" picker, shown only for
 *   instruments marked `single_filter: true` (facilities.yaml) — one filter
 *   per exposure, unlike a simultaneous multi-band imager like GROND.
 * - `imagesWrap`: "which lensed image(s)", shown only for a narrow aperture
 *   (long-slit spectroscopy, or JWST's small-FOV NIRSpec IFU); imaging and a
 *   wide-field IFU like MUSE cover the whole lens system. Hidden = cleared.
 * - `allowed()`: returns the current track (or null). When set, facilities,
 *   instruments and modes are restricted via `trackInstruments` and the
 *   track's `modes:` (plan §6.2 — instruments are data, not code).
 * Returns `{ refresh }` so the caller can re-apply after the track changes.
 */
function wireFacilityCascade({ facilities, facilitySelect, instrumentSelect, modeSelect, filterSelect, filterWrap, imagesWrap, allowed }) {
  const allowedPairs = () => {
    const track = allowed?.();
    return track ? new Set(trackInstruments(track, facilities).map(({ facility, instrument }) => `${facility.id}/${instrument.id}`)) : null;
  };
  function currentInstrument() {
    const facility = facilityById(facilities, facilitySelect.value);
    return facility?.instruments?.find((i) => i.id === instrumentSelect.value);
  }
  function updateFacilities() {
    const pairs = allowedPairs();
    const usable = pairs ? facilities.filter((f) => (f.instruments ?? []).some((i) => pairs.has(`${f.id}/${i.id}`))) : facilities;
    const previous = facilitySelect.value;
    facilitySelect.innerHTML = optionEls(
      usable.map((f) => f.id),
      usable.map((f) => f.name),
    );
    if (usable.some((f) => f.id === previous)) facilitySelect.value = previous;
    updateInstruments();
  }
  function updateInstruments() {
    const pairs = allowedPairs();
    const facility = facilityById(facilities, facilitySelect.value);
    const instruments = (facility?.instruments ?? []).filter((i) => !pairs || pairs.has(`${facility.id}/${i.id}`));
    instrumentSelect.innerHTML = optionEls(
      instruments.map((i) => i.id),
      instruments.map((i) => i.name),
    );
    if (modeSelect) updateModes();
    if (filterSelect) updateFilter();
  }
  function updateModes() {
    const instrument = currentInstrument();
    const trackModes = allowed?.()?.modes;
    let modes = instrument?.modes?.length ? instrument.modes : INSTRUMENT_MODES;
    if (trackModes?.length && modes.some((m) => trackModes.includes(m))) modes = modes.filter((m) => trackModes.includes(m));
    modeSelect.innerHTML = optionEls(modes);
    if (imagesWrap) updateImages();
  }
  function updateImages() {
    const show = modeSelect.value === 'spectroscopy' || (facilitySelect.value === 'jwst' && modeSelect.value === 'ifu');
    imagesWrap.hidden = !show;
    if (!show) imagesWrap.querySelector('input').value = '';
  }
  function updateFilter() {
    const instrument = currentInstrument();
    const show = Boolean(instrument?.single_filter && instrument.filters?.length);
    filterWrap.hidden = !show;
    filterSelect.innerHTML = show ? optionEls(instrument.filters) : '';
  }
  facilitySelect.addEventListener('change', updateInstruments);
  instrumentSelect.addEventListener('change', () => {
    if (modeSelect) updateModes();
    if (filterSelect) updateFilter();
  });
  if (modeSelect && imagesWrap) modeSelect.addEventListener('change', updateImages);
  updateFacilities();
  return { refresh: updateFacilities };
}

function wireChangeStatusForm(container, ctx, candidate, rules, knownTasks) {
  const form = container.querySelector('#status-form');
  if (!form) return;
  const select = form.querySelector('select[name="newStatus"]');
  const extra = form.querySelector('#status-extra-fields');
  const errorEl = form.querySelector('#status-error');

  select.addEventListener('change', () => {
    const requires = requiredFieldsFor(rules, select.value);
    const requiredHtml = requires
      .map((field) => {
        const vocab = vocabulary(rules, field);
        if (vocab.length) {
          return `<label>${escapeHtml(field)} <select name="extra:${field}">${vocab.map((v) => `<option value="${escapeHtml(v.id)}">${escapeHtml(v.label)}</option>`).join('')}</select></label>`;
        }
        return `<label>${escapeHtml(field)} <input name="extra:${field}" required /></label>`;
      })
      .join('');
    // Roles that become relevant in the target phase (rules.yaml `roles:`) —
    // e.g. lens_modeling_lead + data_manager on confirmation — can be set in
    // the same action. Optional: leaving "Group default" keeps people.yaml's.
    const newRoles = select.value ? newlyRelevantRoles(rules, candidate.data.status, select.value, ctx.people) : [];
    const overrides = candidate.data.roles_override ?? {};
    const rolesHtml = newRoles.length
      ? `<fieldset class="phase-roles"><legend>Roles now relevant in ${escapeHtml(getStatus(rules, select.value)?.label ?? select.value)} <span class="muted">(optional)</span></legend>${newRoles
          .map((roleId) => `<label>${escapeHtml(roleId)} ${roleSelect(`roleov:${roleId}`, roleId, ctx.people, overrides[roleId])}</label>`)
          .join('')}</fieldset>`
      : '';
    // Moving on while this phase's tracks still have open tasks: warn (never block).
    let leaveNote = '';
    if (select.value && !isTerminal(rules, select.value) && !isBackwardTransition(rules, candidate.data.status, select.value)) {
      const phaseTrackIds = new Set(tracksForStatus(rules, candidate.data.status).map((t) => t.id));
      leaveNote = openTasksWarning(knownTasks.filter((t) => phaseTrackIds.has(t.data?.track)), 'Note: in the current phase,');
    }
    // Moving into / out of a finished status also closes / reopens the issue.
    let closeNote = '';
    if (select.value && isTerminal(rules, select.value) && !isTerminal(rules, candidate.data.status)) {
      const why = getStatus(rules, select.value)?.close_comment;
      closeNote = `<p class="muted">This also closes the candidate issue on GitHub${why ? ` — "${escapeHtml(why)}"` : ''}; its tasks are left as they are.</p>${openTasksWarning(knownTasks, 'Note:')}`;
    } else if (select.value && !isTerminal(rules, select.value) && isTerminal(rules, candidate.data.status)) {
      closeNote = '<p class="muted">This also reopens the candidate issue on GitHub.</p>';
    }
    extra.innerHTML = requiredHtml + rolesHtml + leaveNote + closeNote;
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    const newStatusId = select.value;
    if (!newStatusId) {
      errorEl.textContent = 'Choose a status.';
      return;
    }
    const values = Object.fromEntries(new FormData(form).entries());
    const extraFields = {};
    for (const [key, value] of Object.entries(values)) {
      if (key.startsWith('extra:')) extraFields[key.slice('extra:'.length)] = value;
    }
    // Role choices from the "now relevant" prompt ride along in the same
    // write: changeCandidateStatus merges extra fields flat, so this full
    // object replaces roles_override (a "Group default" choice removes a pin).
    if (form.querySelector('select[name^="roleov:"]')) {
      extraFields.roles_override = collectRoleOverrides(form, candidate.data.roles_override ?? {});
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    try {
      // changeCandidateStatus already returns the fully-updated data —
      // patch it into `candidate` directly (same object reference inside
      // ctx.candidates) rather than trusting an immediate refreshCandidates
      // to reflect a write we just made: its listCandidateIssues call has
      // the same label-filtered-list propagation lag we already hit for
      // new-candidate and add-task, so it could just as easily hand back
      // the pre-change body and make the update look like it "did nothing".
      const nextData = await changeCandidateStatus(ctx.client, ctx.config.dataRepo, candidate, newStatusId, extraFields, rules);
      candidate.data = nextData;
      candidate.issue = { ...candidate.issue, state: issueStateFor(rules, newStatusId).state };
      const { tasks, comments } = await loadCandidateDetail(ctx.client, candidate);
      renderCandidatePage(container, ctx, candidate, mergeTasks(tasks, knownTasks), comments);
    } catch (err) {
      errorEl.textContent = `Could not update status: ${err.message}`;
      submitBtn.disabled = false;
    }
  });
}

function wireAddTaskForm(container, ctx, candidate, facilities, tasks, comments) {
  const form = container.querySelector('#add-task-form');
  if (!form) return;
  const typeSelect = form.querySelector('select[name="type"]');
  const trackSelect = form.querySelector('select[name="track"]');
  const imageSelect = form.querySelector('select[name="image"]');
  const imageWrap = form.querySelector('[data-role="image-wrap"]');
  const trackHint = form.querySelector('[data-role="track-hint"]');
  const cadenceInput = form.querySelector('input[name="cadenceDays"]');
  const errorEl = form.querySelector('#add-task-error');
  const mailtoEl = container.querySelector('#trigger-mailto');

  const currentTrack = () =>
    trackSelect?.value && trackSelect.value !== UNTRACKED ? ctx.rules.tracks?.find((t) => t.id === trackSelect.value) ?? null : null;

  const triggerCascade = wireFacilityCascade({
    facilities,
    facilitySelect: form.querySelector('fieldset[data-type="trigger"] [data-role="facility"]'),
    instrumentSelect: form.querySelector('fieldset[data-type="trigger"] [data-role="instrument"]'),
    modeSelect: form.querySelector('fieldset[data-type="trigger"] [data-role="mode"]'),
    filterSelect: form.querySelector('fieldset[data-type="trigger"] [data-role="filter"]'),
    filterWrap: form.querySelector('fieldset[data-type="trigger"] [data-role="filter-wrap"]'),
    imagesWrap: form.querySelector('fieldset[data-type="trigger"] [data-role="images-wrap"]'),
    allowed: currentTrack,
  });
  function showType(type) {
    typeSelect.value = type;
    form.querySelectorAll('fieldset[data-type]').forEach((fs) => {
      fs.hidden = fs.dataset.type !== type;
    });
  }
  typeSelect.addEventListener('change', () => {
    showType(typeSelect.value);
    mailtoEl.innerHTML = '';
    updateHint();
  });

  // Choosing a track pre-fills type, cadence, the instrument choices and (via
  // its role) the assignee, and asks for the target image on per-image tracks.
  function applyTrack(preselectImage) {
    const track = currentTrack();
    if (track?.task_type) showType(track.task_type);
    if (cadenceInput) cadenceInput.value = track?.default_cadence_days ?? '';
    triggerCascade.refresh();

    if (imageWrap) {
      const eligible = track?.per_image ? eligibleImages(track, candidate.data, tasks) : [];
      imageWrap.hidden = !track?.per_image;
      imageSelect.innerHTML = eligible.length
        ? optionEls(
            eligible.map((e) => e.image),
            eligible.map((e) => `${e.image} (detected ${e.detected})${e.targeted ? ` — ${e.taskCount} task${e.taskCount === 1 ? '' : 's'} so far` : ''}`),
          )
        : '<option value="">no trailing image detected yet</option>';
      if (preselectImage) imageSelect.value = preselectImage;
    }
    updateHint();
  }

  // Who the task will be assigned to — depends on the track (its role) and the type (triggers add the PI).
  function updateHint() {
    const track = currentTrack();
    if (trackHint) {
      if (!track?.role) {
        trackHint.textContent = !trackSelect.value
          ? 'Choose the follow-up track this task belongs to (or "Other (untracked)").'
          : track
            ? ''
            : 'Untracked task — it will be listed under "Other tasks", with no default owner.';
      } else {
        const { personId } = resolveRole(candidate.data, track.role, ctx.people);
        const name = personId ? findPerson(ctx.people, personId)?.name ?? personId : 'nobody (role unassigned)';
        trackHint.textContent =
          typeSelect.value === 'trigger'
            ? `Will be assigned to the chosen facility's PI and ${name} (${track.role}).`
            : `Will be assigned to ${name} (${track.role}).`;
      }
    }
  }
  if (trackSelect) {
    trackSelect.addEventListener('change', () => applyTrack());
    applyTrack();
  }

  // "+ Add task to this track" / "+ Add for image X" buttons on the track cards.
  container.querySelectorAll('[data-add-task]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!trackSelect) return;
      trackSelect.value = btn.dataset.track;
      applyTrack(btn.dataset.image);
      form.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    mailtoEl.innerHTML = '';

    const type = typeSelect.value;
    const values = Object.fromEntries(new FormData(form).entries());
    const candidateId = candidate.data.id;
    if (trackSelect && !trackSelect.value) {
      errorEl.textContent = 'Choose a track first (or "Other (untracked)").';
      return;
    }
    const track = currentTrack();
    const image = track?.per_image ? values.image || null : null;
    if (track?.per_image && !image) {
      errorEl.textContent = 'Choose which trailing image this task targets (record its detection in the Image timeline first).';
      return;
    }
    const trackFields = { track: track?.id ?? null, image };

    const typeFields =
      type === 'trigger'
        ? {
            facility: values.facility,
            instrument: values.instrument,
            mode: values.mode,
            filterBand: values.filterBand || null,
            requestedDate: values.requestedDate,
            cadenceDays: values.cadenceDays === '' || values.cadenceDays == null ? null : Number(values.cadenceDays),
            until: values.until || null,
            images: (values.images ?? '').split(',').map((s) => s.trim()).filter(Boolean),
          }
        : type === 'archival'
          ? {
              source: (values.source ?? '').trim(),
              instrument: (values.archivalInstrument ?? '').trim(),
              dateStart: values.dateStart || null,
              dateEnd: values.dateEnd || null,
              dataLocation: (values.archivalLocation ?? '').trim(),
              reductionStatus: values.archivalReduction,
            }
          : type === 'analysis'
            ? {
                product: values.product,
                result: values.result,
                files: values.files.split(',').map((s) => s.trim()).filter(Boolean),
              }
            : {
                summary: values.summary,
                deadline: values.deadline,
                options: values.options.split(',').map((s) => s.trim()).filter(Boolean),
              };
    if (type === 'archival' && !typeFields.source) {
      errorEl.textContent = 'Say where the archival data comes from (Source), e.g. LSST.';
      return;
    }
    const fields = { ...trackFields, ...typeFields };
    // A per-image trigger covers its target image unless told otherwise.
    if (type === 'trigger' && image && !fields.images.length) fields.images = [image];

    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    try {
      const { title, body, labels, assignees } = buildTaskIssue(candidateId, type, fields, {
        rules: ctx.rules,
        candidate: candidate.data,
        peopleData: ctx.people,
        facilities,
      });
      const issue = await createIssue(ctx.client, ctx.config.dataRepo, { title, body, labels, assignees });

      // Build the banner HTML now but only inject it into the DOM *after*
      // re-rendering below (into the fresh #trigger-mailto element) —
      // otherwise it would be wiped out the instant it appeared.
      let bannerHtml = '';
      if (type === 'trigger') {
        const facility = facilityById(facilities, fields.facility);
        const instrument = facility?.instruments?.find((i) => i.id === fields.instrument);
        const visibilityTonight =
          facility && !facility.space_based
            ? nightlyVisibility({
                raDeg: candidate.data.ra_deg,
                decDeg: candidate.data.dec_deg,
                site: facility.site,
                minAltitudeDeg: facility.min_altitude_deg,
                maxAirmass: facility.max_airmass,
                rules: ctx.rules,
              })
            : null;
        const { url: mailtoUrl, to, cc } = buildTriggerMailto({
          candidate: candidate.data,
          facility,
          instrument,
          mode: fields.mode,
          visibilityTonight,
          peopleData: ctx.people,
        });
        const toName = escapeHtml(to?.name ?? facility?.contact?.pi ?? 'the PI');
        const ccText = cc.length ? ` (cc: ${cc.map((p) => escapeHtml(p.name)).join(', ')})` : '';
        bannerHtml = `<p class="card">Created <a href="${issue.html_url}" target="_blank" rel="noreferrer">#${issue.number}</a>. <a href="${mailtoUrl}">✉️ Email ${toName}${ccText} about this trigger</a></p>`;
      }
      const dropped = issue.droppedAssignees ?? [];
      if (dropped.length || (issue.assignees ?? []).length === 0) {
        const names = dropped.map((id) => `${findPerson(ctx.people, id)?.name ?? id} (${id})`).join(' and ');
        const unassigned = (issue.assignees ?? []).length === 0;
        const reminder = dropped.length
          ? `Task created, but GitHub wouldn't assign ${names} — only collaborators on the data repo can be assigned (a placeholder GitHub username in people.yaml can't). ${unassigned ? 'Please assign someone by hand' : 'Please add them by hand once they have access'}${type === 'trigger' ? ' and email the PI (above)' : ''}.`
          : type === 'trigger'
            ? 'No assignee is set on this task — please email the PI (above) and assign someone responsible for the follow-up.'
            : "No assignee is set on this task yet — consider assigning someone so it doesn't get lost.";
        bannerHtml += `<p class="card unassigned">⚠️ ${escapeHtml(reminder)}</p>`;
      }

      // loadCandidateDetail's label-filtered list lags behind recent creates
      // (see CLAUDE.md) and can omit this task *and* one added a minute ago —
      // merge the refetch with every task this page already knew about.
      const fresh = await loadCandidateDetail(ctx.client, candidate);
      const merged = mergeTasks(fresh.tasks, [buildTaskFromIssue(issue), ...tasks]);
      renderCandidatePage(container, ctx, candidate, merged, fresh.comments);
      if (bannerHtml) {
        const freshMailtoEl = container.querySelector('#trigger-mailto');
        if (freshMailtoEl) freshMailtoEl.innerHTML = bannerHtml;
      }
    } catch (err) {
      errorEl.textContent = `Could not create task: ${err.message}`;
      submitBtn.disabled = false;
    }
  });
}

function wireRolesForm(container, ctx, candidate, tasks, comments) {
  const form = container.querySelector('#roles-form');
  if (!form) return;
  const view = container.querySelector('#roles-view');
  const editBtn = container.querySelector('#edit-roles-btn');
  const errorEl = form.querySelector('#roles-error');
  const setEditing = (on) => {
    form.hidden = !on;
    view.hidden = on;
    editBtn.hidden = on;
  };
  editBtn.addEventListener('click', () => setEditing(true));
  form.querySelector('#cancel-roles-btn').addEventListener('click', () => setEditing(false));

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    const current = candidate.data.roles_override ?? {};
    const next = collectRoleOverrides(form, current);
    if (JSON.stringify(next) === JSON.stringify(current)) {
      setEditing(false);
      return;
    }
    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    try {
      const mainLeadBefore = resolveRole(candidate.data, 'main_lead', ctx.people).personId;
      // `replace` so a role set back to "Group default" is actually removed.
      candidate.data = await updateCandidateFields(ctx.client, ctx.config.dataRepo, candidate, { roles_override: next }, { replace: ['roles_override'] });
      // The main lead is the candidate issue's GitHub assignee — keep it in sync.
      const mainLeadAfter = resolveRole(candidate.data, 'main_lead', ctx.people).personId;
      let notice = '';
      if (mainLeadAfter !== mainLeadBefore) {
        const { dropped } = await setIssueAssignees(ctx.client, ctx.config.dataRepo, candidate.issue.number, mainLeadAfter ? [mainLeadAfter] : []);
        if (dropped.length) {
          notice = `Roles saved, but GitHub wouldn't assign ${escapeHtml(findPerson(ctx.people, dropped[0])?.name ?? dropped[0])} (${escapeHtml(dropped[0])}) to the candidate issue — only collaborators on the data repo can be assigned (a placeholder username can't).`;
        }
      }
      renderCandidatePage(container, ctx, candidate, tasks, comments);
      if (notice) container.querySelector('#roles-view')?.insertAdjacentHTML('afterbegin', `<p class="card unassigned">⚠️ ${notice}</p>`);
    } catch (err) {
      errorEl.textContent = `Could not save roles: ${err.message}`;
      submitBtn.disabled = false;
    }
  });
}

function wireMoveToTrack(container, ctx, candidate, tasks, comments) {
  const errorEl = container.querySelector('#move-task-error');
  container.querySelectorAll('[data-move-task]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const number = Number(btn.dataset.moveTask);
      const trackId = container.querySelector(`[data-move-select="${number}"]`)?.value;
      if (!trackId) {
        if (errorEl) errorEl.textContent = 'Pick a track to move the task into.';
        return;
      }
      const task = tasks.find((t) => t.issue.number === number);
      btn.disabled = true;
      try {
        const moved = await setTaskTrack(ctx.client, ctx.config.dataRepo, task, trackId, ctx.rules);
        // Use the write's own result rather than refetching (CLAUDE.md).
        const nextTasks = tasks.map((t) => (t.issue.number === number ? moved : t));
        renderCandidatePage(container, ctx, candidate, nextTasks, comments);
      } catch (err) {
        if (errorEl) errorEl.textContent = `Could not move #${number}: ${err.message}`;
        btn.disabled = false;
      }
    });
  });
}

/**
 * "+ Log observation" on a trigger's task line opens a small form right under
 * it; saving appends one night to the trigger's `observations` and re-renders
 * from the write's own result (CLAUDE.md: no refetch).
 */
function wireLogObservation(container, ctx, candidate, tasks, comments) {
  container.querySelectorAll('[data-log-obs]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const line = btn.closest('li.task-line');
      const existing = line.nextElementSibling?.classList.contains('log-obs-row') ? line.nextElementSibling : null;
      if (existing) {
        existing.remove();
        btn.setAttribute('aria-expanded', 'false');
        return;
      }
      btn.setAttribute('aria-expanded', 'true');
      const number = Number(btn.dataset.logObs);
      const row = document.createElement('li');
      row.className = 'log-obs-row';
      row.innerHTML = `
        <form class="log-obs-form">
          <label>Obs. UTC * ${utcDateTimeInputHtml('obsUtc')}</label>
          <label>Setup <input name="setup" placeholder="e.g. slit 1.0 arcsec, 4x600s / g,r,i" /></label>
          <label>Conditions <input name="conditions" placeholder="e.g. clear, seeing 0.8 arcsec" /></label>
          <label>Data location <input name="dataLocation" placeholder="URL, archive id or path" /></label>
          <label>Reduction status <select name="reductionStatus">${optionEls(REDUCTION_STATUSES)}</select></label>
          <div class="form-actions">
            <button type="submit">Save observation</button>
            <button type="button" class="linklike" data-cancel>Cancel</button>
            <span class="error"></span>
          </div>
        </form>`;
      line.after(row);
      const form = row.querySelector('form');
      const errorEl = form.querySelector('.error');
      form.querySelector('[data-cancel]').addEventListener('click', () => {
        row.remove();
        btn.setAttribute('aria-expanded', 'false');
      });
      form.querySelector('input[name="obsUtc"]').focus();
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const values = Object.fromEntries(new FormData(form).entries());
        const obsUtc = utcDateTimeValue(values.obsUtc);
        if (!obsUtc || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obsUtc)) {
          errorEl.textContent = 'Enter when the data was taken, as yyyy-mm-dd hh:mm (UTC).';
          return;
        }
        const submitBtn = form.querySelector('button[type="submit"]');
        submitBtn.disabled = true;
        try {
          const task = tasks.find((t) => t.issue.number === number);
          const updated = await appendTriggerObservation(ctx.client, ctx.config.dataRepo, task, {
            obs_utc: obsUtc,
            setup: values.setup.trim(),
            conditions: values.conditions.trim(),
            data_location: values.dataLocation.trim(),
            reduction_status: values.reductionStatus,
          });
          renderCandidatePage(container, ctx, candidate, tasks.map((t) => (t.issue.number === number ? updated : t)), comments);
        } catch (err) {
          errorEl.textContent = `Could not save: ${err.message}`;
          submitBtn.disabled = false;
        }
      });
    });
  });
}

function wireImageTimelineForm(container, ctx, candidate, tasks, comments) {
  const form = container.querySelector('#image-timeline-form');
  if (!form) return;
  const errorEl = form.querySelector('#image-timeline-error');

  // Predicted arrival follows the delay (and the reference date) as you type.
  form.addEventListener('input', () => {
    const values = Object.fromEntries(new FormData(form).entries());
    const ref = referenceImage(candidate.data);
    const anchor = arrivalAnchor(candidate.data, { [ref]: { detected: values[`detected:${ref}`] || null } });
    form.querySelectorAll('[data-arrival]').forEach((el) => {
      const delay = (values[`delay:${el.dataset.arrival}`] ?? '').trim();
      el.textContent = dateFromDelay(anchor, delay === '' ? null : delay) ?? '—';
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    const values = Object.fromEntries(new FormData(form).entries());
    const current = candidate.data.image_dates ?? {};
    const patch = {};
    const setField = (image, field, raw) => {
      const value = raw || null;
      if ((current[image]?.[field] ?? null) !== value) {
        patch[image] = { ...(patch[image] ?? {}), [field]: value };
      }
    };

    for (const image of imageLabels(candidate.data)) {
      for (const field of IMAGE_FIELDS) setField(image, field, values[`${field}:${image}`]);
    }
    const newImage = (values.newImage ?? '').trim().toUpperCase();
    if (newImage) {
      if (!/^[A-Z][A-Z0-9]{0,2}$/.test(newImage)) {
        errorEl.textContent = 'New image label should be a letter, optionally followed by up to two letters/digits (e.g. E, A2).';
        return;
      }
      if (imageLabels(candidate.data).includes(newImage)) {
        errorEl.textContent = `Image ${newImage} is already listed — edit its row instead.`;
        return;
      }
      patch[newImage] = { detected: null, peak: null, faded: null };
      for (const field of IMAGE_FIELDS) patch[newImage][field] = values[`${field}:__new`] || null;
    }
    if (!newImage && (values['delay:__new'] || values['delay_err:__new'])) {
      errorEl.textContent = 'Give the new image a label (first column) to save its time delay.';
      return;
    }

    // Estimated time delays (± 1σ) go straight into time_delays.predicted /
    // predicted_err; an empty box removes that entry.
    const ref = referenceImage(candidate.data);
    const td = candidate.data.time_delays ?? {};
    const predicted = { ...(td.predicted ?? {}) };
    const predictedErr = { ...(td.predicted_err ?? {}) };
    const delayImages = [...imageLabels(candidate.data).filter((i) => i !== ref).map((i) => [i, i]), ...(newImage ? [[newImage, '__new']] : [])];
    for (const [image, key] of delayImages) {
      for (const [raw, target, label, min] of [
        [values[`delay:${key}`], predicted, 'Time delay', -Infinity],
        [values[`delay_err:${key}`], predictedErr, 'Time-delay uncertainty', 0],
      ]) {
        const text = (raw ?? '').trim();
        if (text === '') delete target[image];
        else if (!Number.isFinite(Number(text)) || Number(text) < min) {
          errorEl.textContent = `${label} for image ${image} should be a number of days${min === 0 ? ' (≥ 0)' : ''}, e.g. 2.5.`;
          return;
        } else target[image] = Number(text);
      }
    }
    const sameMap = (a = {}, b = {}) => JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort());
    const delaysChanged = !sameMap(predicted, td.predicted) || !sameMap(predictedErr, td.predicted_err);

    if (!Object.keys(patch).length && !delaysChanged) {
      errorEl.textContent = 'Nothing changed.';
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    try {
      // Use the write's own result rather than refetching (CLAUDE.md).
      const fieldsPatch = { image_dates: patch };
      // time_delays is replaced whole when predictions change, so a cleared prediction is really removed.
      if (delaysChanged) fieldsPatch.time_delays = { ...td, predicted, predicted_err: predictedErr };
      candidate.data = await updateCandidateFields(ctx.client, ctx.config.dataRepo, candidate, fieldsPatch, {
        replace: delaysChanged ? ['time_delays'] : [],
      });
      renderCandidatePage(container, ctx, candidate, tasks, comments);
    } catch (err) {
      errorEl.textContent = `Could not save the image timeline: ${err.message}`;
      submitBtn.disabled = false;
    }
  });
}

// Only one candidate page is on screen at a time; its "refresh when the tab
// becomes visible again" listener is replaced on every render().
let onVisible = null;

function markDirty(event) {
  event.currentTarget.dataset.dirty = '1';
}

export async function render(container, ctx, params) {
  const { candidates, client } = ctx;
  const candidate = findCandidate(candidates, params.id);

  if (onVisible) document.removeEventListener('visibilitychange', onVisible);
  onVisible = null;

  if (!candidate) {
    container.innerHTML = `<p class="error">No candidate found for "${escapeHtml(params.id)}".</p>`;
    return;
  }

  container.innerHTML = '<p class="muted">Loading candidate…</p>';
  const { tasks, comments } = await loadCandidateDetail(client, candidate);
  renderCandidatePage(container, ctx, candidate, tasks, comments);

  // Coming back to this tab (e.g. after closing a task on GitHub) re-reads
  // GitHub — unless the user has started filling in a form here, which a
  // re-render would wipe; then the ↻ Refresh button is the way.
  const hash = window.location.hash;
  onVisible = () => {
    if (document.visibilityState !== 'visible' || window.location.hash !== hash || !container.isConnected) return;
    if (container.dataset.dirty === '1') return;
    refreshCandidatePage(container, ctx, candidate);
  };
  document.addEventListener('visibilitychange', onVisible);
}

/** Re-read the candidate issue, its tasks and comments from GitHub and re-render. */
async function refreshCandidatePage(container, ctx, candidate) {
  const button = container.querySelector('#refresh-candidate');
  if (button) {
    button.disabled = true;
    button.textContent = '↻ Refreshing…';
  }
  try {
    await refreshCandidate(ctx.client, candidate);
    const { tasks, comments } = await loadCandidateDetail(ctx.client, candidate);
    renderCandidatePage(container, ctx, candidate, tasks, comments);
  } catch (err) {
    if (button) {
      button.disabled = false;
      button.textContent = '↻ Refresh';
    }
    container.querySelector('#refresh-error')?.replaceChildren(`Could not refresh: ${err.message}`);
  }
}

/**
 * The actual DOM build + wiring, split out from `render()` so a caller
 * that already knows the full task list (e.g. wireAddTaskForm, right
 * after creating one) can render it directly instead of going through
 * another `loadCandidateDetail` — see the race-condition note on
 * `listCandidateSubIssues` where this is called.
 */
function renderCandidatePage(container, ctx, candidate, tasks, comments) {
  const { facilities, people, rules } = ctx;
  const data = candidate.data;
  const status = data ? getStatus(rules, data.status) : null;

  const rolesSection = data ? renderRolesCard(data, rules, people) : '';

  const nextStepsList = data
    ? nextStepsFor(rules, data.status)
        .map((s) => `<li>${escapeHtml(s)}</li>`)
        .join('')
    : '';

  container.innerHTML = `
    <h1>${escapeHtml(data?.id ?? candidate.issue.title)} ${statusPillHtml(status)}</h1>
    <p class="muted">
      ${data ? `Main lead: <strong>${escapeHtml(findPerson(people, resolveRole(data, 'main_lead', people).personId)?.name ?? 'unassigned')}</strong> · ` : ''}
      ${escapeHtml(data?.tns_name ?? '')} ·
      <a href="${candidate.issue.html_url}" target="_blank" rel="noreferrer">issue #${candidate.issue.number}</a>
      · <button type="button" class="linklike" id="refresh-candidate" title="Re-read this candidate and its tasks from GitHub (also happens automatically when you return to this tab)">↻ Refresh</button>
      <span class="muted">updated ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
      <span id="refresh-error" class="error"></span>
    </p>

    ${rolesSection}
    ${data ? renderChangeStatus(rules, data.status, data, tasks) : ''}

    <h2>Visibility</h2>
    ${renderVisibility(data, facilities, rules)}

    <div class="card">
      <h2>Suggested next steps</h2>
      <ul class="next-steps">${nextStepsList || '<li class="muted">None listed for this status.</li>'}</ul>
    </div>

    ${data ? renderImageTimeline(data) : ''}

    ${data ? renderTrackSection(ctx, data, tasks) : `<h2>Tasks</h2><div class="card">${renderTaskLines(tasks, ctx)}</div>`}
    ${data ? renderAddTask(facilities, tracksForStatus(rules, data.status), rules) : ''}

    <h2>Observation log</h2>
    ${renderObservationLog(tasks, ctx)}

    <h2>Discussion</h2>
    ${renderComments(comments)}

    ${candidate.notes ? `<h2>Notes</h2><div class="card">${escapeHtml(candidate.notes)}</div>` : ''}
  `;

  // Any typing in a form marks the page dirty, so a tab-refocus refresh won't wipe it.
  container.dataset.dirty = '0';
  container.addEventListener('input', markDirty); // same function reference, so re-renders never stack it
  container.querySelector('#refresh-candidate')?.addEventListener('click', () => refreshCandidatePage(container, ctx, candidate));

  if (data) {
    wireChangeStatusForm(container, ctx, candidate, rules, tasks);
    wireRolesForm(container, ctx, candidate, tasks, comments);
    wireImageTimelineForm(container, ctx, candidate, tasks, comments);
    wireMoveToTrack(container, ctx, candidate, tasks, comments);
    wireLogObservation(container, ctx, candidate, tasks, comments);
    wireAddTaskForm(container, ctx, candidate, facilities, tasks, comments);
  }
}
